import { useState, useId } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowRight, MapPin, BadgeX, Clock, AlertTriangle, TrendingDown,
  ScanLine, ShieldCheck, Brain, Zap, Globe, Lock,
  CheckCircle2, XCircle, ChevronDown, ChevronUp,
} from 'lucide-react';
import { CompanyLogoOrInitials } from '@/components/ui/CompanyLogoOrInitials';
import { getPrimarySource } from '@/data/sourceRegistry';

// ─── Demo video URLs (S3 public bucket) ─────────────────────────────────────
const VIDEO_EN = 'https://reevol-insights-assets.s3.amazonaws.com/videos/reevol-demo-en.mp4';
const VIDEO_ZH = 'https://reevol-insights-assets.s3.amazonaws.com/videos/reevol-demo-zh.mp4';

// ─── Types ───────────────────────────────────────────────────────────────────

interface PublicCompanyData {
  id: string;
  companyName: string;
  companyWebsite: string | null;
  industry: string | null;
  description: string | null;
  totalEmployees: string | null;
  yearEstablished: string | null;
  country: string | null;
  sources: string[];
  hasLogo: boolean;
}

// ─── Translations ─────────────────────────────────────────────────────────────

const CONTENT = {
  en: {
    langToggle: '中文',
    signIn: 'Sign in',
    badge: 'Reevol × Trade Intelligence',
    heroLabel: '100% Free — Now and Forever',
    heroHeadline: (name: string, conf: string) =>
      <>We noticed <span className="text-[#523EF7]">{name}</span> at {conf}</>,
    heroGeneric: 'Qualify every trade show lead in seconds',
    heroPitch: (conf: string) =>
      `As a ${conf} exhibitor, you meet hundreds of potential buyers. Reevol helps you instantly qualify every one of them — before you leave the booth.`,
    heroPitchGeneric: "Reevol gives exhibitors instant KYB-grade intelligence on every buyer who hands you a card — directly from the booth.",
    heroCta: "Get Started — It's Free",
    trustLabel: 'Trusted at leading conferences',
    trustedAt: ['Canton Fair', 'Global Sources', 'HKTDC', 'CES', 'Ambiente'],
    problem: {
      title: "The Exhibitor's Blind Spot",
      subtitle: 'You invest thousands in conference booths — but walk away with a stack of business cards and zero buyer insight.',
      items: [
        { title: 'Business Cards Tell You Nothing', body: 'Name and company — that\'s it. No buyer background, no company credibility signals, no risk indicators.' },
        { title: 'Manual Research Takes Weeks', body: 'After the conference, your team spends days Googling companies and requesting buyer reports — while competitors are already closing deals.' },
        { title: 'Risky Buyers Slip Through', body: 'Without real-time risk data, you quote terms to companies that default, pay late, or disappear entirely.' },
        { title: 'Lost Revenue, Wasted Effort', body: 'Your best leads get cold while you\'re chasing bad ones. The ROI on your booth investment suffers.' },
      ],
    },
    video: {
      title: 'See How It Works',
      subtitle: 'Watch how exhibitors turn a card scan into a full buyer intelligence profile in 10 seconds.',
    },
    solution: {
      title: 'Verify Every Buyer Before You Quote',
      subtitle: 'Reevol transforms a simple card scan into a full buyer intelligence profile — powered by AI and global trade data. Always free.',
      features: [
        { title: 'Instant Card Scanning', body: 'Point your phone at any conference badge or business card. Our AI extracts contact, company, and role data in under 2 seconds.' },
        { title: 'Real-Time Buyer Intelligence Profile', body: 'Get a buyer intelligence score, company credibility signals, and company background insights pulled from global trade databases — instantly.' },
        { title: 'AI Risk Assessment', body: 'Our proprietary model gives each lead a risk score, with plain-language explanations of key risk factors.' },
        { title: 'Smart Lead Prioritization', body: 'Leads are automatically ranked by buyer credibility and deal potential. Focus on the buyers that matter.' },
        { title: 'Global Coverage', body: 'Data on 300M+ companies across 190 countries. Works for any conference, any industry, anywhere.' },
        { title: 'Secure & Private', body: 'Enterprise-grade encryption and ensured data privacy. Your lead data is always protected.' },
      ],
    },
    howItWorks: {
      title: 'From Card to Buyer Profile in 10 Seconds',
      steps: [
        { n: '01', title: 'Scan the Card', body: 'Open the app, point your camera at any conference badge or business card. AI reads it instantly.' },
        { n: '02', title: 'AI Enriches the Lead', body: 'We match the company against global trade, credibility, and financial databases in real-time.' },
        { n: '03', title: 'Get Buyer Intelligence', body: 'View buyer intelligence score, risk grade, credibility signals, and AI recommendations — all on your phone.' },
        { n: '04', title: 'Prioritize & Act', body: 'Your lead list auto-ranks by quality. Export to CRM or share with your sales team instantly.' },
      ],
    },
    comparison: {
      title: 'Reevol vs. The Old Way',
      colFeature: 'Capability',
      colOld: 'Manual Process',
      colNew: 'Reevol',
      rows: [
        { f: 'Lead capture', old: 'Collect business cards', nw: 'AI card scan — 2 seconds' },
        { f: 'Company research', old: 'Days of manual Googling', nw: 'Instant AI enrichment' },
        { f: 'Buyer check', old: 'Request reports post-show', nw: 'Real-time buyer intelligence' },
        { f: 'Risk assessment', old: 'Gut feeling', nw: 'AI-powered risk scoring' },
        { f: 'Lead prioritization', old: 'Spreadsheet sorting', nw: 'Automatic smart ranking' },
        { f: 'Team sharing', old: 'Email chains & files', nw: 'One-tap CRM export' },
        { f: 'Coverage', old: 'Limited to known contacts', nw: '300M+ companies globally' },
        { f: 'Cost', old: 'Expensive buyer reports', nw: '100% free — forever' },
      ],
    },
    outcomes: {
      title: 'Results That Matter',
      stats: [
        { value: '10×', label: 'Faster lead qualification' },
        { value: '87%', label: 'Reduction in identity fraud' },
        { value: '2 sec', label: 'Card to full profile' },
        { value: '300M+', label: 'Companies in database' },
      ],
      quote: 'We used to leave conferences with a stack of cards and no idea who was worth pursuing. Reevol changed everything — we now know which buyers to prioritize before we even leave the booth.',
      author: 'Head of Sales',
      company: 'Global Electronics Manufacturer',
    },
    faq: {
      title: 'Frequently Asked Questions',
      stillHave: 'Still have questions?',
      contactUs: 'Contact Us',
      categories: [
        { name: 'Product', items: [
          { q: 'What conferences does it work at?', a: 'Reevol works at any trade show, conference, or exhibition worldwide. Our card-scanning AI is trained on 50+ badge and card formats and adapts to new designs automatically.' },
          { q: 'How accurate is the buyer intelligence?', a: 'Our buyer intelligence profiles are built from the same data sources used by major trade credit insurers. The AI risk model has been validated against 2+ years of actual trade outcomes with 94% accuracy.' },
          { q: 'Does it work offline?', a: 'Card scanning works offline. Buyer intelligence enrichment requires a data connection, but results are cached locally so you can review profiles anytime.' },
          { q: 'What data do I get for each lead?', a: 'Company name, registration details, buyer intelligence score, company credibility signals, company background insights, AI risk score, risk factor explanations, and recommended engagement terms.' },
        ]},
        { name: 'Pricing', items: [
          { q: 'Is it really free?', a: 'Yes — Reevol is a free service. No credit card required, no trial period, no hidden fees. We believe exhibitors deserve buyer intelligence without barriers.' },
          { q: 'Why is it free?', a: 'Reevol is leading the Revenue Evolution for Global Trade. Conference Leads Intelligence is part of our mission to make trade intelligence accessible to every exhibitor worldwide.' },
          { q: 'Will it stay free?', a: "Yes. Reevol Conference Leads Intelligence is 100% free, now and forever. That's our guarantee." },
          { q: 'Can I use it for my whole team?', a: 'Absolutely. Each account supports unlimited team members. Invite your entire sales team at no cost.' },
        ]},
        { name: 'Security & Privacy', items: [
          { q: 'Is my data secure?', a: 'Absolutely. All data is encrypted at rest and in transit with enterprise-grade encryption. Your lead data is never shared with third parties.' },
          { q: 'Who can see my scanned leads?', a: 'Only you and team members you explicitly invite. Your lead data, notes, and scan history are completely private to your account.' },
          { q: 'Can I delete my data?', a: 'Yes — you can delete individual leads or your entire account at any time. Deletion is permanent and immediate.' },
        ]},
      ],
    },
    bottomCta: {
      title: (conf: string) => `Start qualifying your ${conf} leads today`,
      subtitle: 'Free to start. No credit card required.',
      primary: 'Create Free Account',
      secondary: 'Already have an account? Sign in',
    },
    footer: {
      tagline: 'Leading the Revenue Evolution for Global Trade',
      copyright: '40Seas Inc. All rights reserved.',
      links: [
        { label: 'Privacy Policy', href: 'https://reevol.com/privacy-policy' },
        { label: 'Terms of Service', href: 'https://reevol.com/terms-and-conditions' },
        { label: 'Contact', href: 'mailto:contact@reevol.com' },
      ],
    },
  },
  zh: {
    langToggle: 'English',
    signIn: '登录',
    badge: 'Reevol × 贸易情报',
    heroLabel: '100% 终身免费',
    heroHeadline: (name: string, conf: string) =>
      <>我们注意到 <span className="text-[#523EF7]">{name}</span> 参加了{conf}</>,
    heroGeneric: '数秒内完成每一条展会线索的资质评估',
    heroPitch: (conf: string) =>
      `作为 ${conf} 的参展商，您在展位上会遇到数百位潜在买家。Reevol 帮助您在离开展位前即时完成每一位买家的资质核查。`,
    heroPitchGeneric: 'Reevol 为参展商提供即时 KYB 级买家情报 — 覆盖每一张递给您的名片，直接在展位现场完成。',
    heroCta: '免费获取',
    trustLabel: '深受主流展会认可',
    trustedAt: ['广交会', '环球资源', 'HKTDC', 'CES', 'Ambiente'],
    problem: {
      title: '参展商的盲区',
      subtitle: '您在展会上投入了大量资源，最后只带回一沓名片，对买家一无所知。',
      items: [
        { title: '名片信息形同虚设', body: '只有姓名和公司 — 没有买家背景，没有公司可信度信号，没有风险指标。' },
        { title: '人工调研耗时良久', body: '展会结束后，团队花费数天搜索公司信息和请求买家报告 — 而竞争对手已经在签约了。' },
        { title: '高风险买家悄然混入', body: '没有实时风险数据，您向违约、拖欠或消失的公司报价。' },
        { title: '成本投入血本无归', body: '最好的线索变冷了，因为您在追逐不良客户。展位投资回报率下降。' },
      ],
    },
    video: {
      title: '了解工作原理',
      subtitle: '观看参展商如何在10秒内将名片扫描转化为完整的买家情报档案。',
    },
    solution: {
      title: '报价前，先验证每一位买家',
      subtitle: 'Reevol 将简单的名片扫描转化为完整的买家情报档案 — 由 AI 与全球贸易数据支撑。终身免费使用。',
      features: [
        { title: 'AI 极速扫描名片', body: '将手机对准任何展会胸牌或名片。AI 在2秒内提取联系人、公司和职位信息。' },
        { title: '实时生成买家情报档案', body: '从全球贸易数据库中即时获取买家情报评分、公司可信度信号和公司背景洞察。' },
        { title: 'AI 智能风险评估', body: '专有模型为每条线索提供风险评分，并用通俗语言解释关键风险因素。' },
        { title: '智能线索优先排序', body: '线索按买家可信度和交易潜力自动排名。专注于真正重要的买家。' },
        { title: '全球企业全面覆盖', body: '覆盖190个国家超过3亿家公司。适用于任何展会、任何行业、任何地点。' },
        { title: '数据安全', body: '企业级加密，确保数据隐私。您的线索数据始终受到保护。' },
      ],
    },
    howItWorks: {
      title: '10秒内从名片到买家档案',
      steps: [
        { n: '01', title: '扫描名片', body: '打开应用，将摄像头对准任何展会胸牌或名片。AI 即时读取。' },
        { n: '02', title: 'AI 数据充实', body: '实时匹配全球贸易、可信度和财务数据库中的公司信息。' },
        { n: '03', title: '获取买家情报', body: '手机端一键查看买家情报评分、风险等级、可信度信号和 AI 建议，信息一目了然。' },
        { n: '04', title: '优先排序与行动', body: '线索列表按质量自动排序。一键导出到 CRM 或分享给销售团队。' },
      ],
    },
    comparison: {
      title: 'Reevol 对比传统方式',
      colFeature: '功能',
      colOld: '传统流程',
      colNew: 'Reevol',
      rows: [
        { f: '线索采集', old: '收集名片', nw: 'AI 名片扫描 — 2秒' },
        { f: '公司调研', old: '数天人工搜索', nw: '即时 AI 数据充实' },
        { f: '买家检查', old: '展后申请报告', nw: '实时买家情报档案' },
        { f: '风险评估', old: '凭直觉判断', nw: 'AI 驱动风险评分' },
        { f: '线索排序', old: '电子表格排序', nw: '自动智能排名' },
        { f: '团队共享', old: '邮件往来和文件', nw: '一键导出 CRM' },
        { f: '覆盖范围', old: '仅限已知联系人', nw: '全球3亿+公司' },
        { f: '费用', old: '昂贵的买家报告', nw: '100% 终身免费' },
      ],
    },
    outcomes: {
      title: '真正有意义的成果',
      stats: [
        { value: '10×', label: '更快的线索评估' },
        { value: '87%', label: '身份欺诈降低' },
        { value: '2秒', label: '名片到完整档案' },
        { value: '3亿+', label: '数据库中的公司' },
      ],
      quote: '我们过去在展会结束后带走一堆名片，却不知道哪些值得跟进。Reevol 改变了一切 — 我们现在在离开展位之前就知道该优先联系哪些买家。',
      author: '销售总监',
      company: '全球电子制造商',
    },
    faq: {
      title: '常见问题',
      stillHave: '还有其他问题？',
      contactUs: '联系我们',
      categories: [
        { name: '产品', items: [
          { q: '适用于哪些展会？', a: 'Reevol 适用于全球任何贸易展、会议或展览。我们的名片扫描 AI 已针对50+种胸牌和名片格式进行训练，并自动适应新设计。' },
          { q: '买家情报准确吗？', a: '我们的买家情报档案使用与主要贸易信用保险公司相同的数据源。AI 风险模型已通过2年以上的实际贸易结果验证，准确率达94%。' },
          { q: '可以离线使用吗？', a: '名片扫描可离线使用。买家情报充实需要数据连接，但结果会本地缓存，随时可查看档案。' },
          { q: '每条线索能获得哪些数据？', a: '公司名称、注册信息、买家情报评分、公司可信度信号、公司背景洞察、AI 风险评分、风险因素说明和建议的合作条款。' },
        ]},
        { name: '定价', items: [
          { q: '真的免费吗？', a: '是的 — Reevol 是免费服务。无需信用卡，没有试用期，没有隐藏费用。我们相信参展商值得无障碍获取买家情报。' },
          { q: '为什么免费？', a: 'Reevol 致力于引领全球贸易的收入革命。Conference Leads Intelligence 是我们让全球每位参展商都能获得贸易情报使命的一部分。' },
          { q: '会一直免费吗？', a: '是的。Reevol Conference Leads Intelligence 终身免费，全程保障。这是我们的承诺。' },
          { q: '可以让整个团队使用吗？', a: '当然可以。每个账户支持无限数量的团队成员。免费邀请您的整个销售团队。' },
        ]},
        { name: '安全与隐私', items: [
          { q: '我的数据安全吗？', a: '绝对安全。所有数据在存储和传输中均采用企业级加密。您的线索数据绝不会与第三方共享。' },
          { q: '谁能看到我扫描的线索？', a: '只有您和您明确邀请的团队成员。您的线索数据、笔记和扫描记录对您的账户完全私密。' },
          { q: '可以删除我的数据吗？', a: '可以 — 您可以随时删除单条线索或整个账户。删除是永久且即时的。' },
        ]},
      ],
    },
    bottomCta: {
      title: (conf: string) => `立即开始筛选您的${conf}线索`,
      subtitle: '免费开始。无需信用卡。',
      primary: '免费创建账户',
      secondary: '已有账户？立即登录',
    },
    footer: {
      tagline: '引领全球贸易的收入革命',
      copyright: '40Seas Inc. 版权所有。',
      links: [
        { label: '隐私政策', href: 'https://reevol.com/privacy-policy' },
        { label: '服务条款', href: 'https://reevol.com/terms-and-conditions' },
        { label: '联系我们', href: 'mailto:contact@reevol.com' },
      ],
    },
  },
} as const;

type Lang = keyof typeof CONTENT;

// ─── Helper components ────────────────────────────────────────────────────────

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#523EF7] mb-3">
      {children}
    </p>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="text-2xl sm:text-3xl font-bold text-[#222222] tracking-tight mb-4">
      {children}
    </h2>
  );
}

const PROBLEM_ICONS = [BadgeX, Clock, AlertTriangle, TrendingDown];
const SOLUTION_ICONS = [ScanLine, ShieldCheck, Brain, Zap, Globe, Lock];

// ─── FAQ accordion ────────────────────────────────────────────────────────────

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  const id = useId();
  return (
    <div className="border-b border-[#DDDDDD] last:border-none">
      <button
        className="w-full flex items-center justify-between py-4 text-left gap-4"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-controls={id}
      >
        <span className="text-sm font-semibold text-[#222222]">{q}</span>
        {open ? <ChevronUp size={16} className="text-[#9A9A9A] flex-shrink-0" /> : <ChevronDown size={16} className="text-[#9A9A9A] flex-shrink-0" />}
      </button>
      {open && (
        <div id={id} className="pb-4 text-sm text-[#3F3F3F] leading-relaxed">
          {a}
        </div>
      )}
    </div>
  );
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────

function HeroSkeleton() {
  return (
    <div className="animate-pulse space-y-4 flex flex-col items-center">
      <div className="flex items-center gap-4">
        <div className="w-24 h-24 rounded-xl bg-[#E8E8E8]" />
        <div className="text-3xl text-[#DDDDDD]">×</div>
        <div className="w-24 h-24 rounded-xl bg-[#E8E8E8]" />
      </div>
      <div className="h-10 bg-[#E8E8E8] rounded-xl w-96 max-w-full" />
      <div className="h-5 bg-[#E8E8E8] rounded-xl w-72 max-w-full" />
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function OutreachLandingPage() {
  const [searchParams] = useSearchParams();
  const atlasId = searchParams.get('id');
  const langParam = searchParams.get('lang');
  const lang: Lang = langParam === 'zh' ? 'zh' : 'en';
  const t = CONTENT[lang];

  const { data: company, isLoading } = useQuery<PublicCompanyData>({
    queryKey: ['public-company', atlasId],
    queryFn: () => fetch(`/api/public/company/${atlasId}`).then(r => { if (!r.ok) throw new Error('Not found'); return r.json(); }),
    enabled: !!atlasId,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const [faqTab, setFaqTab] = useState(0);

  const source = company ? getPrimarySource(company.sources) : null;
  const conferenceName = source?.displayName ?? (lang === 'zh' ? '展会' : 'trade show');
  const signupHref = ['/signup', atlasId && `id=${atlasId}`, lang === 'zh' && 'lang=zh'].filter(Boolean).join('?').replace('/?', '?');
  const loginHref = ['/login', atlasId && `id=${atlasId}`, lang === 'zh' && 'lang=zh'].filter(Boolean).join('?').replace('/?', '?');

  // Lang toggle preserves id param
  const toggleLangHref = `/lp?${[atlasId && `id=${atlasId}`, lang === 'en' ? 'lang=zh' : ''].filter(Boolean).join('&')}`;

  return (
    <div className="min-h-screen bg-[#F2F2F2] flex flex-col" lang={lang === 'zh' ? 'zh-CN' : 'en'}>

      {/* ── Sticky header ── */}
      <header className="bg-white border-b border-[#DDDDDD] px-6 py-4 flex items-center justify-between sticky top-0 z-20">
        <img src="/reevol-dark.png" alt="Reevol" className="h-14" />
        <div className="flex items-center gap-4">
          <Link to={toggleLangHref} className="text-xs font-semibold text-[#9A9A9A] hover:text-[#523EF7] transition-colors">
            {t.langToggle}
          </Link>
          <Link to={loginHref} className="text-sm font-medium text-[#523EF7] hover:underline">
            {t.signIn}
          </Link>
        </div>
      </header>

      <main className="flex-1 flex flex-col items-center">

        {/* ── Hero ── */}
        <section className="w-full max-w-3xl mt-12 mb-10 px-4">
          <div className="glass-card px-8 py-12 flex flex-col items-center text-center gap-6">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#523EF7]">
              {t.badge}
            </p>
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 rounded-full px-3 py-1">
              {t.heroLabel}
            </span>

            {isLoading && <HeroSkeleton />}

            {!isLoading && company && (
              <>
                <div className="flex items-center gap-5">
                  <img src="/reevol-dark.png" alt="Reevol" className="h-12 object-contain" />
                  {company.hasLogo && (
                    <>
                      <span className="text-2xl text-[#BBBBBB] font-light select-none">×</span>
                      <CompanyLogoOrInitials company={company} size="lg" />
                    </>
                  )}
                </div>
                <h1 className="text-3xl sm:text-4xl font-bold text-[#222222] tracking-tight leading-tight max-w-xl">
                  {t.heroHeadline(company.companyName, conferenceName)}
                </h1>
                <p className="text-[#3F3F3F] text-lg max-w-lg leading-relaxed">
                  {t.heroPitch(conferenceName)}
                </p>
                {source && (source.city || source.country) && (
                  <span className="inline-flex items-center gap-1.5 text-sm font-medium text-[#523EF7] bg-[#523EF7]/8 rounded-full px-4 py-1.5">
                    <MapPin size={14} />
                    {[source.displayName, source.city, source.country].filter(Boolean).join(' · ')}
                  </span>
                )}
              </>
            )}

            {!isLoading && !company && (
              <>
                <img src="/reevol-dark.png" alt="Reevol" className="h-14" />
                <h1 className="text-3xl sm:text-4xl font-bold text-[#222222] tracking-tight leading-tight max-w-xl">
                  {t.heroGeneric}
                </h1>
                <p className="text-[#3F3F3F] text-lg max-w-lg leading-relaxed">
                  {t.heroPitchGeneric}
                </p>
              </>
            )}

            <div className="flex flex-col sm:flex-row items-center gap-3 mt-2">
              <Link to={signupHref} className="auth-btn-primary inline-flex items-center gap-2 px-8 py-3">
                {t.heroCta}
                <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </section>

        {/* ── Video ── */}
        <section id="video" className="w-full max-w-3xl px-4 mb-12">
          <div className="glass-card overflow-hidden">
            <div className="px-8 pt-8 pb-4 text-center">
              <SectionLabel>{t.video.title}</SectionLabel>
              <SectionTitle>{t.video.title}</SectionTitle>
              <p className="text-[#3F3F3F] max-w-lg mx-auto text-sm mt-1">{t.video.subtitle}</p>
            </div>
            <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
              <video
                key={lang}
                className="absolute inset-0 w-full h-full object-cover"
                src={lang === 'zh' ? VIDEO_ZH : VIDEO_EN}
                controls
                playsInline
                preload="metadata"
              />
            </div>
          </div>
        </section>

        {/* ── Trust strip ── */}
        <section className="w-full max-w-3xl px-4 mb-12">
          <p className="text-xs font-semibold uppercase tracking-[0.15em] text-[#9A9A9A] text-center mb-4">
            {t.trustLabel}
          </p>
          <div className="flex flex-wrap justify-center gap-x-8 gap-y-2">
            {t.trustedAt.map((name) => (
              <span key={name} className="text-sm font-semibold text-[#BBBBBB]">{name}</span>
            ))}
          </div>
        </section>

        {/* ── Problem ── */}
        <section className="w-full max-w-3xl px-4 mb-12">
          <div className="glass-card px-8 py-10">
            <div className="text-center mb-8">
              <SectionLabel>{t.problem.title}</SectionLabel>
              <h2 className="text-2xl sm:text-3xl font-bold text-[#222222] tracking-tight">{t.problem.title}</h2>
              <p className="text-[#3F3F3F] mt-2 max-w-lg mx-auto">{t.problem.subtitle}</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {t.problem.items.map((item, i) => {
                const Icon = PROBLEM_ICONS[i];
                return (
                  <div key={i} className="flex gap-4">
                    <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center text-red-500 flex-shrink-0 mt-0.5">
                      <Icon size={18} />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-[#222222] mb-1">{item.title}</h3>
                      <p className="text-sm text-[#3F3F3F] leading-relaxed">{item.body}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>


        {/* ── Solution ── */}
        <section className="w-full max-w-3xl px-4 mb-12">
          <div className="text-center mb-6">
            <SectionLabel>{t.solution.title}</SectionLabel>
            <SectionTitle>{t.solution.title}</SectionTitle>
            <p className="text-[#3F3F3F] max-w-lg mx-auto">{t.solution.subtitle}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {t.solution.features.map((f, i) => {
              const Icon = SOLUTION_ICONS[i];
              return (
                <div key={i} className="glass-card p-5 flex gap-4">
                  <div className="w-10 h-10 rounded-xl bg-[#523EF7]/10 flex items-center justify-center text-[#523EF7] flex-shrink-0">
                    <Icon size={18} />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-[#222222] mb-1">{f.title}</h3>
                    <p className="text-sm text-[#3F3F3F] leading-relaxed">{f.body}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* ── How It Works ── */}
        <section id="how-it-works" className="w-full max-w-3xl px-4 mb-12">
          <div className="glass-card px-8 py-10">
            <div className="text-center mb-8">
              <SectionLabel>{t.howItWorks.title}</SectionLabel>
              <SectionTitle>{t.howItWorks.title}</SectionTitle>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {t.howItWorks.steps.map((step) => (
                <div key={step.n} className="flex gap-4">
                  <div className="w-10 h-10 rounded-full bg-[#523EF7] text-white text-sm font-bold flex items-center justify-center flex-shrink-0">
                    {step.n}
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-[#222222] mb-1">{step.title}</h3>
                    <p className="text-sm text-[#3F3F3F] leading-relaxed">{step.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Comparison ── */}
        <section className="w-full max-w-3xl px-4 mb-12">
          <div className="text-center mb-6">
            <SectionLabel>{t.comparison.title}</SectionLabel>
            <SectionTitle>{t.comparison.title}</SectionTitle>
          </div>
          <div className="glass-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#DDDDDD]">
                    <th className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wider text-[#9A9A9A]">{t.comparison.colFeature}</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wider text-[#9A9A9A]">{t.comparison.colOld}</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wider text-[#523EF7]">{t.comparison.colNew}</th>
                  </tr>
                </thead>
                <tbody>
                  {t.comparison.rows.map((row, i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-[#F7F7F7]' : 'bg-white'}>
                      <td className="px-5 py-3 font-medium text-[#222222]">{row.f}</td>
                      <td className="px-5 py-3 text-[#9A9A9A] flex items-center gap-1.5">
                        <XCircle size={14} className="text-red-400 flex-shrink-0" />
                        {row.old}
                      </td>
                      <td className="px-5 py-3 text-[#523EF7] font-medium">
                        <span className="flex items-center gap-1.5">
                          <CheckCircle2 size={14} className="flex-shrink-0" />
                          {row.nw}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* ── Results That Matter ── */}
        <section className="w-full max-w-3xl px-4 mb-12">
          <div className="text-center mb-6">
            <SectionLabel>{t.outcomes.title}</SectionLabel>
            <SectionTitle>{t.outcomes.title}</SectionTitle>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            {t.outcomes.stats.map((stat) => (
              <div key={stat.value} className="glass-card p-5 text-center">
                <p className="text-3xl font-bold text-[#523EF7] mb-1">{stat.value}</p>
                <p className="text-xs text-[#9A9A9A] leading-tight">{stat.label}</p>
              </div>
            ))}
          </div>
          {/* Testimonial */}
          <div className="glass-card px-8 py-8 text-center">
            <p className="text-base text-[#3F3F3F] italic leading-relaxed mb-5 max-w-2xl mx-auto">
              "{t.outcomes.quote}"
            </p>
            <p className="text-sm font-semibold text-[#222222]">{t.outcomes.author}</p>
            <p className="text-xs text-[#9A9A9A]">{t.outcomes.company}</p>
          </div>
        </section>

        {/* ── FAQ ── */}
        <section className="w-full max-w-3xl px-4 mb-12">
          <div className="text-center mb-6">
            <SectionTitle>{t.faq.title}</SectionTitle>
          </div>
          <div className="glass-card overflow-hidden">
            {/* Category tabs */}
            <div className="flex border-b border-[#DDDDDD]">
              {t.faq.categories.map((cat, i) => (
                <button
                  key={cat.name}
                  onClick={() => setFaqTab(i)}
                  className={`flex-1 py-3 text-xs font-semibold transition-colors ${
                    faqTab === i
                      ? 'text-[#523EF7] border-b-2 border-[#523EF7] -mb-px'
                      : 'text-[#9A9A9A] hover:text-[#3F3F3F]'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>
            <div className="px-6 py-2">
              {t.faq.categories[faqTab].items.map((item) => (
                <FaqItem key={item.q} q={item.q} a={item.a} />
              ))}
            </div>
            <div className="px-6 pb-5 pt-2 flex items-center gap-2 text-sm text-[#9A9A9A]">
              {t.faq.stillHave}
              <a href="mailto:contact@reevol.com" className="font-semibold text-[#523EF7] hover:underline">
                {t.faq.contactUs}
              </a>
            </div>
          </div>
        </section>

        {/* ── Bottom CTA ── */}
        <section className="w-full max-w-3xl px-4 mb-16">
          <div className="glass-card px-8 py-12 flex flex-col items-center text-center gap-4">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#222222] tracking-tight">
              {t.bottomCta.title(conferenceName)}
            </h2>
            <p className="text-[#9A9A9A] text-base">{t.bottomCta.subtitle}</p>
            <Link to={signupHref} className="auth-btn-primary inline-flex items-center gap-2 px-8 py-3">
              {t.bottomCta.primary}
              <ArrowRight size={16} />
            </Link>
            <Link to={loginHref} className="text-sm text-[#9A9A9A] hover:text-[#523EF7] transition-colors">
              {t.bottomCta.secondary}
            </Link>
          </div>
        </section>

      </main>

      {/* ── Footer ── */}
      <footer className="bg-white border-t border-[#DDDDDD] px-6 py-8">
        <div className="max-w-3xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex flex-col items-center sm:items-start gap-1">
            <img src="/reevol-dark.png" alt="Reevol" className="h-12" />
            <p className="text-xs text-[#9A9A9A]">{t.footer.tagline}</p>
          </div>
          <div className="flex flex-wrap justify-center gap-x-5 gap-y-1">
            {t.footer.links.map((link) => (
              <a key={link.label} href={link.href} className="text-xs text-[#9A9A9A] hover:text-[#523EF7] transition-colors">
                {link.label}
              </a>
            ))}
          </div>
        </div>
        <p className="text-center text-xs text-[#BBBBBB] mt-4">
          © {new Date().getFullYear()} {t.footer.copyright}
        </p>
      </footer>

    </div>
  );
}
